import { type FC, useState } from "react";
import { createPortal } from "react-dom";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { Separator } from "@jti/core-components";
import { Menu, MenuItem } from "@jti/core-components/components/Menu";

import { type AccountDropdownSettings } from "../../Header";
import { useLogOut } from "../../hooks/useLogOut";

import { getGreetingTitle } from "./utils/getGreetingTitle";
import { getAccountLinks } from "./utils/getProfileDropdownLink";

import styles from "./UserActions.module.scss";

export const USER_DIALOG_SELECTOR = "[data-user-dialog]";

type UserDialogProps = {
  setIsUserDialogOpen: (isOpen: boolean) => void;
  accountDropdownSettings?: AccountDropdownSettings;
  userName?: string;
  onLogOut: () => void;
  isEnabledUserIndicationWithName: boolean;
  portalContainer?: HTMLElement | null;
};

export const UserDialog: FC<UserDialogProps> = ({
  setIsUserDialogOpen,
  userName,
  onLogOut,
  accountDropdownSettings,
  isEnabledUserIndicationWithName,
  portalContainer,
}) => {
  const { __ } = useTranslations();
  const [isPending, setIsPending] = useState(false);
  const handleLogOut = useLogOut(onLogOut, setIsPending);

  if (!accountDropdownSettings) {
    return null;
  }

  const { logoutLabel } = accountDropdownSettings;

  const handleLinkClick = () => {
    setIsUserDialogOpen(false);
  };

  const userDialogLinks = getAccountLinks(accountDropdownSettings);

  const title = getGreetingTitle(userName, accountDropdownSettings);

  const isRenderedInHeaderPortal = Boolean(portalContainer);

  const dialog = (
    <Menu
      className={classNames({
        [styles.userDialog]: !isRenderedInHeaderPortal,
        [styles.userDialogFullWidth]: isRenderedInHeaderPortal,
        [styles.userDialogRight]: isEnabledUserIndicationWithName && !isRenderedInHeaderPortal,
      })}
      data-user-dialog
      title={title}
      onClose={() => setIsUserDialogOpen(false)}
      closeButtonLabel={__("common.close")}
      onClick={(e) => e.stopPropagation()}
    >
      {userDialogLinks.map(({ href, label, nativeBehaviour }) => (
        <MenuItem
          key={`${label}-${href}`}
          label={label}
          href={href}
          nativeBehaviour={nativeBehaviour}
          onClick={handleLinkClick}
        />
      ))}
      <Separator />
      <MenuItem label={logoutLabel ?? ""} onClick={handleLogOut} disabled={isPending} />
    </Menu>
  );

  return portalContainer ? createPortal(dialog, portalContainer) : dialog;
};
